import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

interface Props {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onStartDateChange: (date: Dayjs | null) => void;
  onEndDateChange: (date: Dayjs | null) => void;
  onSearch?: () => void;
}

const HSCROLL = {
  display: 'flex',
  alignItems: 'center',
  gap: { xs: 1.5, md: 2 },
  flexWrap: 'nowrap',        // 줄바꿈 금지
  overflowX: 'auto',         // 가로 스크롤
  minWidth: 0,
  '& > *': { flex: '0 0 auto' },
  scrollbarWidth: 'thin',
  '::-webkit-scrollbar': { height: 6 },
  '::-webkit-scrollbar-thumb': { bgcolor: '#c6c6c6', borderRadius: 3 },
} as const;

const DateRangeSelector: React.FC<Props> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => {
  // 오늘 기준 프리셋 설정
  const setLastNDays = (days: number) => {
    const end = dayjs();
    const start = end.subtract(days - 1, 'day');
    onStartDateChange(start.startOf('day'));
    onEndDateChange(end.startOf('day'));
  };

  // 프리셋 선택 상태
  const isPresetSelected = (days: number) => {
    if (!startDate || !endDate) return false;
    const today = dayjs().startOf('day');
    const presetStart = today.subtract(days - 1, 'day');
    return startDate.isSame(presetStart, 'day') && endDate.isSame(today, 'day');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          p: 2.5,
          borderRadius: 3,
          bgcolor: '#ffffff',
          maxWidth: { xs: '100%', md: 'fit-content' },
          width: '100%',
          height: { xs: 'auto', md: 125 },
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
          border: '1px solid #f1f5f9',
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          mb={2}
          color="#475569"
          sx={{ fontSize: '0.95rem' }}
        >
          기간 설정
        </Typography>

        {/* 한 줄 고정 + 가로 스크롤 */}
        <Box sx={HSCROLL}>
          {/* 시작 날짜 */}
          <DatePicker
            value={startDate}
            onChange={onStartDateChange}
            format="YYYY-MM-DD"
            slotProps={{
              textField: {
                variant: 'standard',
                InputProps: {
                  disableUnderline: true,
                  sx: {
                    width: { xs: 175, md: 165 },
                    flexShrink: 0,
                    px: 1.5,
                    py: 0,
                    height: 42,
                    borderRadius: 2.5,
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    color: '#1e293b',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#cbd5e1',
                    },
                    '& input': {
                      p: 0,
                      height: '100%',
                      lineHeight: '42px',
                      fontSize: '0.9rem',
                    },
                    '& .MuiInputAdornment-root': { ml: '4px' },
                    '& svg': { color: '#0044D2', fontSize: 20 },
                  },
                },
              },
            }}
          />

          <Typography fontSize="0.9rem" color="#94a3b8">~</Typography>

          {/* 종료 날짜 */}
          <DatePicker
            value={endDate}
            onChange={onEndDateChange}
            format="YYYY-MM-DD"
            slotProps={{
              textField: {
                variant: 'standard',
                InputProps: {
                  disableUnderline: true,
                  sx: {
                    width: { xs: 175, md: 165 },
                    flexShrink: 0,
                    px: 1.5,
                    py: 0,
                    height: 42,
                    borderRadius: 2.5,
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    color: '#1e293b',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#cbd5e1',
                    },
                    '& input': {
                      p: 0,
                      height: '100%',
                      lineHeight: '42px',
                      fontSize: '0.9rem',
                    },
                    '& .MuiInputAdornment-root': { ml: '4px' },
                    '& svg': { color: '#0044D2', fontSize: 20 },
                  },
                },
              },
            }}
          />

          {/* 프리셋 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'nowrap',
              '& > .MuiButton-root': {
                minWidth: 'auto',
                whiteSpace: 'nowrap',
                px: 1.25,
                height: 40,
                fontWeight: 700,
              },
            }}
          >
            <Button
              variant={isPresetSelected(8) ? 'contained' : 'outlined'}
              onClick={() => setLastNDays(8)}
            >
              최근 1주
            </Button>
            <Button
              variant={isPresetSelected(32) ? 'contained' : 'outlined'}
              onClick={() => setLastNDays(32)}
            >
              최근 1개월
            </Button>
            <Button
              variant={isPresetSelected(91) ? 'contained' : 'outlined'}
              onClick={() => setLastNDays(91)}
            >
              최근 3개월
            </Button>
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangeSelector;
